import moment from "moment-timezone"
import Config from "react-global-configuration"
import {
  getRepeatDisplayText as getProcessRepeatDisplayText,
  getRepeatDisplayShortText as getProcessRepeatDisplayShortText,
} from "./useProcessUtils"
import { useAuth } from "../services"
import { toId } from "./useDataUtils"
import { JOB_PINNING, PROCESS_REPEAT } from "../data"
import { TASK_RESPONSE_TYPE } from "../data/enums"

// DATE
const { NONE, NUMBER, TEXT, PHOTOS, YES_NO_NA, NUMBER_RATING_5, NUMBER_RATING_10 } = TASK_RESPONSE_TYPE

const timeZoneFallback = moment.tz.guess()

const hasExport = (job) => job?.status.completed && job?.process?.repeat.type === PROCESS_REPEAT.ADHOC

const getJobPinningTypeDescription = ({ pinning: { type } }) => {
  switch (type) {
    case JOB_PINNING.COMPLETED:
      return "Until completed"
    case JOB_PINNING.NEXT_INSTANCE:
      return "Until next instance"
    default:
      return null
  }
}

const jobsHaveResponseUploads = (jobs) => {
  if (!jobs || jobs.length === 0) {
    return false
  }

  return jobs.some((job) => jobHasResponseUploads(job))
}

const jobHasResponseUploads = (job) => {
  if (!job?.status?.steps) {
    return false
  }

  return job.status.steps.some((step) => step.responseUploads.length > 0)
}

const isStepValueResponseType = (responseType) =>
  [TEXT, NUMBER, NUMBER_RATING_5, NUMBER_RATING_10, YES_NO_NA].includes(responseType)

const getNavigateToLink = (job, dayDate) =>
  `${getNavigateDateLink(job.pinning?.type && dayDate ? dayDate : job.availableFrom, job.location?.timeZone)}/${
    job.process ? "process" : "task"
  }/${toId(job)}/${toId(job.location)}`
const getNavigateDateLink = (date, timeZone) => `/jobs/${moment.tz(moment(date), timeZone).format("YYYY-MM-DD")}`
const getNavigateToLinkForTypeLocationDayAndID = (type, locationDay, locationId, id) =>
  `/jobs/${locationDay}/${type ? "process" : "task"}/${id}/${locationId}`

const isJobOverdue = ({ dueAt, status: { completed } }) => dueAt && !completed && moment(dueAt).isSameOrBefore(moment())

const useJobUtils = (apolloClient) => {
  const { location, settings, principal: currentUser } = useAuth(apolloClient)
  const { clientKiosk: kiosk } = Config.get()

  const canNavigateToJob = (job) =>
    toId(job.location) === toId(location) || (!kiosk && settings?.locations.some((l) => toId(l) === toId(job.location)))

  const canDeleteNote = (note) => {
    if (toId(note?.author, true) === currentUser?.userID) {
      return true
    }
    if (currentUser?.permissions.job_update_all) {
      return true
    }
    return false
  }

  const getStepStatus = ({ dueAt, step: { step, responseType, skip, completedAt } }) => {
    if (
      !!step &&
      [NONE, TEXT, NUMBER, NUMBER_RATING_5, NUMBER_RATING_10, YES_NO_NA, PHOTOS].includes(responseType) &&
      !!completedAt &&
      !skip
    ) {
      if (!!dueAt && !!completedAt && moment(completedAt).isAfter(moment(dueAt))) return "overdue"
      return "complete"
    }
    if (!!step && skip) {
      return "skipped"
    }
    if (!!step && !completedAt) {
      return "uncomplete"
    }
    return "none"
  }

  const getRepeatDisplayText = (job) => {
    if (job.process) {
      return getProcessRepeatDisplayText(job.process)
    }
  }

  const getRepeatDisplayShortText = (job) => {
    if (job.process) {
      return getProcessRepeatDisplayShortText(job.process)
    }
  }

  const getScheduleFormattedDate = (availableFrom) => {
    const date = moment(availableFrom)
    return date
      .tz(location.timeZone)
      .format(`D MMM YYYY${date.hours() > 0 || date.minutes() > 0 ? " [at] h:mm A" : ""} z`)
  }

  const isAfterToday = (date) => isJobAfterToday(date)
  const isJobAfterToday = (availableFrom) =>
    availableFrom &&
    moment.tz(moment(availableFrom), location?.timeZone || timeZoneFallback).isSameOrAfter(
      moment
        .tz(moment(), location?.timeZone || timeZoneFallback)
        .add(1, "day")
        .startOf("day")
    )

  return {
    canNavigateToJob,
    canDeleteNote,

    getNavigateToLink,
    getNavigateDateLink,
    getNavigateToLinkForTypeLocationDayAndID,
    getRepeatDisplayText,
    getRepeatDisplayShortText,
    getScheduleFormattedDate,
    getJobPinningTypeDescription,

    getStepStatus,
    isStepValueResponseType,

    isAfterToday,
    isJobAfterToday,
    isJobOverdue,
  }
}

export {
  useJobUtils,
  isStepValueResponseType,
  getNavigateToLink,
  isJobOverdue,
  hasExport,
  jobsHaveResponseUploads,
  jobHasResponseUploads,
}
